import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useHasMenu } from '../../../auth/access';
import { amt, errorText, fmtDate, fyToDate, money, Range, reportsApi, TbRow, TrialBalance } from '../accounting.api';
import BooksHeader, { RangeFields } from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';

const num = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;

/**
 * The trial balance for a range within one financial year: the group tree with subtotals,
 * each ledger's opening, period debits and credits, and closing, in Dr / Cr columns. Tally's
 * two extra lines — last years' profit or loss, and opening balances that do not tally —
 * are set apart. A ledger opens its Ledger Book for the same range.
 */
export default function TrialBalancePage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const canBook = useHasMenu('accounts.ledger-book');

  const [range, setRange] = useState<Range>(fyToDate());
  const [showZero, setShowZero] = useState(false);
  const [showOpening, setShowOpening] = useState(true);
  const [showTxn, setShowTxn] = useState(true);
  const [tb, setTb] = useState<TrialBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const load = useCallback(() => {
    if (!companyId || !range.from || !range.to) return;
    setLoading(true);
    setError(null);
    reportsApi
      .trialBalance(companyId, range, showZero)
      .then(setTb)
      .catch((e) => {
        setTb(null);
        setError(errorText(e, 'Could not make the trial balance', 'ACCOUNTING_TRIAL_BALANCE_FAILED'));
      })
      .finally(() => setLoading(false));
  }, [companyId, range, showZero]);

  useEffect(load, [load]);

  // Rows come in tree order, so a single pass can hide everything under a collapsed group.
  const visible = useMemo(() => {
    if (!tb) return [];
    const hidden = new Set<number>();
    return tb.rows.filter((r) => {
      const under = r.parentId !== null && (collapsed.has(r.parentId) || hidden.has(r.parentId));
      if (under && r.kind === 'GROUP' && r.id !== null) hidden.add(r.id);
      return !under;
    });
  }, [tb, collapsed]);

  const groupIds = useMemo(() => (tb ? tb.rows.filter((r) => r.kind === 'GROUP' && r.id !== null).map((r) => r.id as number) : []), [tb]);
  const parents = useMemo(() => new Set(tb?.rows.map((r) => r.parentId).filter((x): x is number => x !== null)), [tb]);

  const toggle = (id: number) =>
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const cols = (r: Pick<TbRow, 'openingDebit' | 'openingCredit' | 'debit' | 'credit' | 'closingDebit' | 'closingCredit'>) => [
    ...(showOpening ? [r.openingDebit, r.openingCredit] : []),
    ...(showTxn ? [r.debit, r.credit] : []),
    r.closingDebit,
    r.closingCredit,
  ];

  const bookLink = (r: TbRow) => `/accounts/ledger-book?ledgerId=${r.id}&from=${tb?.from}&to=${tb?.to}`;

  return (
    <Stack gap={3}>
      <BooksHeader title='TRIAL BALANCE' companies={companies} companyId={companyId} onCompany={setCompanyId} loaded={loaded}>
        <RangeFields value={range} onChange={setRange} />
        <FormControlLabel control={<Switch checked={showZero} onChange={(e) => setShowZero(e.target.checked)} />} label='Zero balances' />
        <FormControlLabel control={<Switch checked={showOpening} onChange={(e) => setShowOpening(e.target.checked)} />} label='Opening' />
        <FormControlLabel control={<Switch checked={showTxn} onChange={(e) => setShowTxn(e.target.checked)} />} label='Transactions' />
      </BooksHeader>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {tb && (
        <>
          <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1}>
            <Stack direction='row' alignItems='center' gap={1.5} flexWrap='wrap'>
              <Typography variant='body2' color='text.secondary'>
                {fmtDate(tb.from)} to {fmtDate(tb.to)}
              </Typography>
              {tb.balanced ? (
                <Chip color='success' icon={<Icon icon='mdi:check-circle' />} label='Balanced ✓' />
              ) : (
                <Chip color='error' icon={<Icon icon='mdi:alert-circle' />} label='Not balanced' />
              )}
            </Stack>
            <Button
              size='small'
              onClick={() => setCollapsed(collapsed.size ? new Set() : new Set(groupIds))}
              startIcon={<Icon icon={collapsed.size ? 'mdi:unfold-more-horizontal' : 'mdi:unfold-less-horizontal'} />}
            >
              {collapsed.size ? 'Expand all' : 'Collapse all'}
            </Button>
          </Stack>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell {...({ rowSpan: 2 } as object)}>Particulars</TableCell>
                  {showOpening && (
                    <TableCell {...({ colSpan: 2 } as object)} align='center'>
                      Opening
                    </TableCell>
                  )}
                  {showTxn && (
                    <TableCell {...({ colSpan: 2 } as object)} align='center'>
                      Transactions
                    </TableCell>
                  )}
                  <TableCell {...({ colSpan: 2 } as object)} align='center'>
                    Closing
                  </TableCell>
                </TableRow>
                <TableRow>
                  {[...(showOpening ? ['Dr', 'Cr'] : []), ...(showTxn ? ['Debit', 'Credit'] : []), 'Dr', 'Cr'].map((h, i) => (
                    <TableCell key={i} align='right'>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell {...({ colSpan: 7 } as object)}>
                      <Typography variant='body2' color='text.secondary'>
                        Nothing to show for this period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((r, i) => {
                  const group = r.kind === 'GROUP';
                  const special = r.kind === 'PNL' || r.kind === 'OPENING_DIFF';
                  return (
                    <TableRow
                      key={`${r.kind}-${r.id ?? i}`}
                      hover
                      sx={{
                        bgcolor: special ? (r.kind === 'OPENING_DIFF' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(156, 39, 176, 0.06)') : group && r.depth === 0 ? 'grey.50' : undefined,
                      }}
                    >
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction='row' alignItems='center' gap={0.5} sx={{ pl: r.depth * 2.5 }}>
                          {group && r.id !== null && parents.has(r.id) ? (
                            <IconButton size='small' onClick={() => toggle(r.id as number)} sx={{ p: 0.25 }}>
                              <Icon icon={collapsed.has(r.id) ? 'mdi:chevron-right' : 'mdi:chevron-down'} />
                            </IconButton>
                          ) : (
                            <Box sx={{ width: 26, flexShrink: 0 }} />
                          )}
                          {r.kind === 'LEDGER' && canBook ? (
                            <Link component={RouterLink} to={bookLink(r)} underline='hover' variant='body2'>
                              {r.name}
                            </Link>
                          ) : (
                            <Typography
                              variant='body2'
                              fontWeight={group || special ? 700 : 400}
                              fontStyle={special ? 'italic' : undefined}
                              color={r.kind === 'OPENING_DIFF' ? 'warning.dark' : undefined}
                            >
                              {r.name}
                            </Typography>
                          )}
                          {r.kind === 'OPENING_DIFF' && (
                            <Chip size='small' color='warning' variant='outlined' label='Check opening balances' sx={{ ml: 1 }} />
                          )}
                        </Stack>
                      </TableCell>
                      {cols(r).map((v, j) => (
                        <TableCell key={j} align='right' sx={{ ...num, fontWeight: group || special ? 700 : 400 }}>
                          {amt(v)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                <TableRow sx={{ bgcolor: 'grey.100', '& td': { borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell sx={{ fontWeight: 700 }}>Grand total</TableCell>
                  {cols(tb).map((v, j) => (
                    <TableCell key={j} align='right' sx={{ ...num, fontWeight: 700 }}>
                      {money(v)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {!tb.balanced && (
            <Alert severity='error'>
              Debits and credits do not agree. Look for a “Difference in opening balances” line, then run the integrity check in Books Settings.
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}
